import { forwardRef, useState, useId, useRef, useEffect, useImperativeHandle, useCallback, type InputHTMLAttributes, type ReactNode } from 'react';
import styles from './Input.module.css';

type TrackingValue = string | number | boolean;

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  hasError?: boolean;
  helpIcon?: ReactNode;
  /**
   * Ícono que se muestra dentro del campo, a la izquierda (p. ej. una lupa).
   */
  leadingIcon?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  hideHelpIconOnStates?: boolean;
  trackEvent?: string;
  trackData?: Record<string, TrackingValue | null | undefined>;
  preventSpaces?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      hasError = false,
      helpIcon,
      leadingIcon,
      size = 'md',
      hideHelpIconOnStates = true,
      className,
      id,
      disabled,
      value,
      defaultValue,
      onFocus,
      onBlur,
      onChange,
      onKeyDown,
      onPaste,
      trackEvent,
      trackData,
      preventSpaces,
      ...props
    },
    ref
  ) => {
    const generatedId = useId();
    const inputId = id || generatedId;
    const [isFocused, setIsFocused] = useState(false);
    const internalRef = useRef<HTMLInputElement>(null);
    
    const shouldPreventSpaces =
      preventSpaces ??
      (props.type === 'email' ||
        props.type === 'password' ||
        props.type === 'tel' ||
        props.inputMode === 'email' ||
        props.inputMode === 'tel');

    // Combinar ref interno con ref externo
    useImperativeHandle(ref, () => internalRef.current!, []);
    
    // Calcular si hay valor basado en props o estado interno
    const getHasValue = useCallback(() => {
      if (value !== undefined) {
        return String(value || '').trim().length > 0;
      }
      if (internalRef.current) {
        return internalRef.current.value.trim().length > 0;
      }
      return String(defaultValue || '').trim().length > 0;
    }, [value, defaultValue]);

    const [hasValue, setHasValue] = useState(() => getHasValue());
    const hasPlaceholder = Boolean(props.placeholder && props.placeholder.length > 0);
    const showHelpIcon = helpIcon && (!hideHelpIconOnStates || (!hasError && !isFocused));

    // Actualizar hasValue cuando cambie el value prop
    useEffect(() => {
      if (value !== undefined) {
        setHasValue(String(value || '').trim().length > 0);
      }
    }, [value]);

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);
      // Actualizar hasValue al perder el foco para componentes no controlados
      if (value === undefined && internalRef.current) {
        setHasValue(internalRef.current.value.trim().length > 0);
      }
      onBlur?.(e);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (shouldPreventSpaces && e.key === ' ') {
        e.preventDefault();
      }
      onKeyDown?.(e);
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
      if (shouldPreventSpaces) {
        const text = e.clipboardData.getData('text');
        if (/\s/.test(text)) {
          e.preventDefault();
          const cleaned = text.replace(/\s/g, '');
          const inputEl = e.currentTarget;
          const start = inputEl.selectionStart ?? 0;
          const end = inputEl.selectionEnd ?? 0;
          const currentValue = inputEl.value;
          const newValue = currentValue.slice(0, start) + cleaned + currentValue.slice(end);

          const nativeSetter = Object.getOwnPropertyDescriptor(
            window.HTMLInputElement.prototype,
            'value'
          )?.set;
          if (nativeSetter) {
            nativeSetter.call(inputEl, newValue);
          } else {
            inputEl.value = newValue;
          }
          inputEl.dispatchEvent(new Event('input', { bubbles: true }));
        }
      }
      onPaste?.(e);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (shouldPreventSpaces && /\s/.test(e.target.value)) {
        e.target.value = e.target.value.replace(/\s/g, '');
      }
      // Actualizar hasValue inmediatamente cuando cambie el valor
      setHasValue(e.target.value.trim().length > 0);
      onChange?.(e);
    };

    const isLabelActive = label && (isFocused || hasValue || hasPlaceholder);
    const hasLabel = Boolean(label);

    const containerClasses = [
      styles.container,
      hasError ? styles.error : '',
      isFocused && !hasError ? styles.focused : '',
      disabled ? styles.disabled : '',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    const inputClasses = [
      styles.input,
      styles[size],
      hasLabel ? styles.hasLabel : '',
      isLabelActive ? styles.labelActive : '',
      leadingIcon ? styles.hasLeadingIcon : '',
    ]
      .filter(Boolean)
      .join(' ');

    const trackingAttributes: Record<string, string> = {};
    if (trackEvent) {
      trackingAttributes['data-track'] = trackEvent;

      if (trackData) {
        for (const [key, value] of Object.entries(trackData)) {
          if (value == null) continue;
          const attrKey = `data-${key.replace(/[A-Z]/g, m => `-${m.toLowerCase()}`)}`;
          trackingAttributes[attrKey] = String(value);
        }
      }
    }

    return (
      <div className={containerClasses}>
        <div className={styles.inputWrapper}>
          {hasLabel && (
            <label
              htmlFor={inputId}
              className={[
                styles.label,
                isLabelActive ? styles.labelActive : '',
                hasError ? styles.labelError : '',
                isFocused && !hasError ? styles.labelFocused : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {label}
            </label>
          )}

          {leadingIcon && <div className={styles.leadingIcon}>{leadingIcon}</div>}

          <input
            ref={internalRef}
            id={inputId}
            className={inputClasses}
            disabled={disabled}
            value={value}
            defaultValue={defaultValue}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            onChange={handleChange}
            aria-invalid={hasError}
            {...trackingAttributes}
            {...props}
          />

          {showHelpIcon && <div className={styles.helpIcon}>{helpIcon}</div>}
        </div>
      </div>
    );
  }
);

Input.displayName = 'Input';
